/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { HTTP_INTERCEPTORS, HttpClient, HttpInterceptor, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';

import { catchError, map, mergeMap } from 'rxjs/operators';
import { ApiSuccessResponse } from '../../../types/Rest';

import { env } from '../../../../environments/environment-factory';
import { LoggerService } from './logger.service';
import { HandleApiResult, ShowError, ShowWarning, ThrowError } from '../../../core/state/notification/notification.actions';
import { Store } from '@ngxs/store';
import { DashGisHttpUrlEncodingCodec } from './url-encoding-codec';

enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE',
}

@Injectable()
export class RestService {
    constructor(
        private http: HttpClient,
        private logger: LoggerService,
        private store: Store,
        @Inject(HTTP_INTERCEPTORS) private interceptors: HttpInterceptor[]
    ) {}

    /**
     * Used for external REST requests
     * @param url
     * @param params
     * @param options
     */
    public get<T>(url, params = null, options = {}): Observable<T> {
        const requestOptions = this.buildOptions(params, options);
        return this.http
            .get<T>(url, requestOptions)
            .pipe(
                catchError((err, caught) => this.restError(url, options, err)),
                map(this.mapResponse.bind(this))
            );
    }

    /**
     * Used for external REST requests
     * @param {string} url
     * @param {object} params
     * @param {any} postData
     * @param {object} options
     */
    public post<T>(
        url: string,
        params: object = null,
        postData: any = null,
        options: object = {}
    ): Observable<T> {
        const requestOptions = this.buildOptions(params, options);
        return this.http
            .post<T>(url, postData, requestOptions)
            .pipe(
                catchError((err, caught) => this.restError(url, options, err)),
                map(this.mapResponse.bind(this))
            );
    }

    /**
     * Used for external REST requests
     * @param {string} url
     * @param {object} params
     * @param {any} postData
     * @param {object} options
     */
    public put<T>(
        url: string,
        params: object = null,
        postData: any = null,
        options: object = {}
    ): Observable<T> {
        const requestOptions = this.buildOptions(params, options);
        return this.http
            .put<T>(url, postData, requestOptions)
            .pipe(
                catchError((err, caught) => this.restError(url, options, err)),
                map(this.mapResponse.bind(this))
            );
    }

    /**
     * Wraps Angular HttpClient
     * @param route unique router for this request, without leading slash.
     * @param queryParams
     * @param {{}} options If not expecting JSON, specify responseType: 'text' (or other, as appropriate).
     * @return {Observable<T>}
     */
    public apiGet<T>(route, queryParams = null, options = {}): Observable<T> {
        return this.request<T>(HttpMethod.GET, `${env.apiBase}/api/${route}`, null, queryParams, options);
    }

    /**
     * Wraps Angular HttpClient
     * @param route unique router for this request, without leading slash.
     * @param {any} postData
     * @param {any} queryParams
     * @param {[opt: string]: any} options If not expecting JSON, specify responseType: 'text' (or other, as
     * appropriate). Can also include skipErrorHandler: true, if the error will be caught further
     * down the observable.
     * @return {Observable<T>}
     */
    public apiPost<T>(route, postData, queryParams = {}, options = {}): Observable<T> {
        return this.request<T>(HttpMethod.POST, `${env.apiBase}/api/${route}`, postData, queryParams, options);
    }

    /**
     * Wraps Angular HttpClient
     * @param route unique route for this request, without leading slash.
     * @param {any} putData
     * @param {any} queryParams
     * @param {[opt: string]: any} options If not expecting JSON, specify responseType: 'text' (or other, as
     * appropriate). Can also include skipErrorHandler: true, if the error will be caught further
     * down the observable.
     * @return {Observable<T>}
     */
    public apiPut<T>(route, putData, queryParams = {}, options = {}): Observable<T> {
        return this.request(HttpMethod.PUT, `${env.apiBase}/api/${route}`, putData, queryParams, options);
    }

    public apiDelete(route, queryParams = {}, options = {}): Observable<boolean> {
        return this.request<boolean>(
            HttpMethod.DELETE,
            `${env.apiBase}/api/${route}`,
            null,
            queryParams,
            options
        );
    }

    public createParams(params: { [param: string]: string | ReadonlyArray<string> }): HttpParams {
        return new HttpParams({
            fromObject: params,
            encoder: new DashGisHttpUrlEncodingCodec(),
        });
    }

    /**
     * Wraps Angular HttpClient or alternately, sends to web worker with fetch capability
     * @param method
     * @param url
     * @param body
     * @param queryParams
     * @param options
     * @private
     */
    private request<T>(
        method: HttpMethod,
        url,
        body: any = null,
        queryParams: any = {},
        options: { [key: string]: any } = {}
    ): Observable<T> {
        const requestOptions = this.buildOptions(queryParams, options);
        let request;
        switch (method) {
            case HttpMethod.GET:
                request = this.http.get<T>(url, requestOptions);
                break;
            case HttpMethod.PUT:
                request = this.http.put<T>(url, body, requestOptions);
                break;
            case HttpMethod.POST:
                request = this.http.post<T>(url, body, requestOptions);
                break;
            case HttpMethod.DELETE:
                request = this.http.delete(url, requestOptions);
                break;
            default:
                throw new Error('Invalid HTTP method specified');
        }
        return request.pipe(
            map((response) => {
                if (response.hasOwnProperty('metadata') && response.hasOwnProperty('content')) {
                    this.handleMeta(response['metadata']);
                    return response['content'];
                }
                return response;
            }),
            mergeMap((response) => {
                if (options.hasOwnProperty('skipErrorHandler') && options['skipErrorHandler'] === true) {
                    return of(response);
                }
                return of(response).pipe(
                    catchError((err, caught) => this.restError(request.url, request.options, err)),
                );
            }),
            map(this.mapResponse.bind(this))
        );
    }

    private restError(route, options, err) {
        this.logger.error('Error caught in rest service', { route, options, err });
        this.store.dispatch(new ThrowError(err.error?.exception));
        return EMPTY;
    }

    private mapResponse<T>(data: T): T|ApiSuccessResponse {
        const apiResponse = data as ApiSuccessResponse;
        if (apiResponse?.success === false) {
            if (apiResponse?.exception) {
                throw new Error(apiResponse.exception.message);
            }
            if (apiResponse?.fatal) {
                throw new Error(apiResponse.message);
            } else {
                this.store.dispatch(new ShowError(apiResponse.message));
            }
            return apiResponse;
        } else {
            return data as T;
        }
    }

    private buildOptions(params, options: { [key: string]: any } = {}) {
        if (params) {
            options.params = this.createParams(params);
        }
        if (!options.responseType) {
            options.responseType = 'json';
        }
        return options;
    }

    private handleMeta(metadata: any) {}
}
